import { Link } from 'react-router-dom';
import { Upload, FileText, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface WelcomeBannerProps {
  userName?: string;
  hasExistingSessions: boolean;
  className?: string;
}

export function WelcomeBanner({ userName, hasExistingSessions, className }: WelcomeBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed && hasExistingSessions) return null;

  // Full welcome for first-time users
  if (!hasExistingSessions) {
    return (
      <Card className={cn('border-primary/20 bg-primary/5', className)}>
        <CardContent className="pt-6 pb-6">
          <div className="flex flex-col items-center text-center gap-4 max-w-lg mx-auto">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold mb-2">
                Automate your DDQ responses
              </h2>
              <p className="text-muted-foreground">
                Upload a questionnaire and get AI-drafted answers in minutes.
                Our system matches answers from your knowledge base and generates
                drafts for the rest.
              </p>
            </div>
            <Button size="lg" asChild>
              <Link to="/upload">
                <Upload className="h-4 w-4 mr-2" />
                Upload your first questionnaire
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Compact banner for returning users
  return (
    <Card className={cn('', className)}>
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Welcome back{userName ? `, ${userName}` : ''}
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" asChild>
              <Link to="/upload">
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                Upload DDQ
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setDismissed(true)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
